import {
  CustomSorting,
  IndexCard,
  Ordering,
  SortingFunction,
  SortingObject,
} from "@/types";

const toTimestamp = (dateStr?: string): number => {
  if (!dateStr) {
    return 0;
  }
  const ts = new Date(dateStr).getTime();
  return isNaN(ts) ? 0 : ts;
};

const isFeaturedAndDateComparator = (
  cardType: "Newsroom" | "CVE",
  cardA: IndexCard,
  cardB: IndexCard,
  option: Exclude<SortingObject, CustomSorting>,
  nowTs: number,
) => {
  const comparator = (cardA: IndexCard, cardB: IndexCard): number => {
    if (cardA?.type !== cardType || cardB?.type !== cardType) {
      return 0;
    }

    // 1. Featured first
    const aFeat = cardA.isFeatured ? 0 : 1;
    const bFeat = cardB.isFeatured ? 0 : 1;
    if (aFeat !== bFeat) {
      return aFeat - bFeat;
    }

    // 2. Non-past before past
    const aTs = toTimestamp(cardA.date);
    const bTs = toTimestamp(cardB.date);
    const aIsPast = aTs < nowTs ? 1 : 0;
    const bIsPast = bTs < nowTs ? 1 : 0;
    if (aIsPast !== bIsPast) {
      return aIsPast - bIsPast;
    }

    // 3. Date ordering
    if (aIsPast && bIsPast) {
      return bTs - aTs; // both past → newest first
    }
    return aTs - bTs; // both future/today → nearest upcoming first
  };

  if (option.order === "descending") {
    return -comparator(cardA, cardB);
  }
  return comparator(cardA, cardB);
};

export const sort = (
  a: string,
  b: string,
  order: Ordering["order"],
): number => {
  const [lhs, rhs] = order === "ascending" ? [a, b] : [b, a];
  if (lhs < rhs) {
    return -1;
  } else if (lhs > rhs) {
    return 1;
  }
  return 0;
};

export const createSortingMapping = (
  options: SortingObject[],
): SortingFunction[] => {
  return (
    options
      ?.map((option) => {
        {
          if (option?.type === "custom") {
            return option?.callback;
          }

          switch (option?.type) {
            case "Event": {
              return (cardA: IndexCard, cardB: IndexCard) => {
                if (
                  cardA?.type !== option?.type ||
                  cardB?.type !== option?.type
                ) {
                  return 0;
                }

                if (option.field === "isFeaturedAndDate") {
                  const nowTs = Date.now();

                  // Returns >= 0 if today/future/ongoing, < 0 if past
                  const compareToNow = (
                    startDate?: string,
                    endDate?: string,
                  ): number => {
                    const startCmp = toTimestamp(startDate) - nowTs;
                    if (startCmp >= 0) {
                      return startCmp; // startDate is today or future
                    }
                    if (!endDate) {
                      return startDate ? 0 : -1; // if no endDate, consider past if startDate is past, otherwise consider it as today
                    }

                    return toTimestamp(endDate) - nowTs; // endDate still running? or past
                  };

                  const comparator = (
                    cardA: IndexCard,
                    cardB: IndexCard,
                  ): number => {
                    if (cardA?.type !== "Event" || cardB?.type !== "Event") {
                      return 0;
                    }

                    // 1. Featured first
                    const isCardAFeatured = cardA.isFeatured ? 0 : 1;
                    const isCardBFeatured = cardB.isFeatured ? 0 : 1;
                    if (isCardAFeatured !== isCardBFeatured) {
                      return isCardAFeatured - isCardBFeatured;
                    }

                    // 2. Non-past before past
                    const isCardAPast =
                      compareToNow(cardA.startDate, cardA.endDate) < 0 ? 1 : 0;
                    const isCardBPast =
                      compareToNow(cardB.startDate, cardB.endDate) < 0 ? 1 : 0;
                    if (isCardAPast !== isCardBPast) {
                      return isCardAPast - isCardBPast;
                    }

                    // 3. Date ordering
                    const aStartTs =
                      toTimestamp(cardA.startDate) ||
                      toTimestamp(cardA.endDate);
                    const bStartTs =
                      toTimestamp(cardB.startDate) ||
                      toTimestamp(cardB.endDate);

                    if (isCardAPast && isCardBPast) {
                      return bStartTs - aStartTs; // both past → newest past first
                    }
                    return aStartTs - bStartTs; // both future/today → nearest upcoming first
                  };

                  if (option.order === "descending") {
                    return -comparator(cardA, cardB);
                  }

                  return comparator(cardA, cardB);
                }

                return sort(
                  String(cardA[option.field]),
                  String(cardB[option.field]),
                  option.order,
                );
              };
            }
            case "Newsroom": {
              return (cardA: IndexCard, cardB: IndexCard) => {
                if (cardA?.type !== "Newsroom" || cardB?.type !== "Newsroom") {
                  return 0;
                }

                if (option.field === "isFeaturedAndDate") {
                  const nowTs = Date.now();
                  return isFeaturedAndDateComparator(
                    "Newsroom",
                    cardA,
                    cardB,
                    option,
                    nowTs,
                  );
                }

                // if date compare as timestamp not a string
                if (option.field === "date") {
                  const aTs = toTimestamp(cardA.date);
                  const bTs = toTimestamp(cardB.date);
                  return option.order === "ascending" ? aTs - bTs : bTs - aTs;
                }

                return sort(
                  String(cardA[option.field]),
                  String(cardB[option.field]),
                  option.order,
                );
              };
            }
            case "CVE": {
              return (cardA: IndexCard, cardB: IndexCard) => {
                if (cardA?.type !== "CVE" || cardB?.type !== "CVE") {
                  return 0;
                }

                if (option.field === "severity") {
                  const severityOrder = [
                    "N/A",
                    "Low",
                    "Medium",
                    "High",
                    "Critical",
                  ];
                  const catA = cardA.categorization?.find(
                    (c) => c.parameterName === "severity",
                  )?.categories;
                  const catB = cardB.categorization?.find(
                    (c) => c.parameterName === "severity",
                  )?.categories;
                  const severityA =
                    (Array.isArray(catA) ? catA[0] : catA) ?? "N/A";
                  const severityB =
                    (Array.isArray(catB) ? catB[0] : catB) ?? "N/A";
                  const indexA = severityOrder.indexOf(severityA);
                  const indexB = severityOrder.indexOf(severityB);
                  return option.order === "ascending"
                    ? indexA - indexB
                    : indexB - indexA;
                }

                if (option.field === "date") {
                  const aTs = toTimestamp(cardA.date);
                  const bTs = toTimestamp(cardB.date);
                  return option.order === "ascending" ? aTs - bTs : bTs - aTs;
                }

                if (option.field === "isFeaturedAndDate") {
                  const nowTs = Date.now();
                  return isFeaturedAndDateComparator(
                    "CVE",
                    cardA,
                    cardB,
                    option,
                    nowTs,
                  );
                }

                return sort(
                  String(cardA[option.field] ?? ""),
                  String(cardB[option.field] ?? ""),
                  option.order,
                );
              };
            }
            case "Solution":
            case "Use Case":
            case "Integration":
            case "Download":
            case "Resource":
            default: {
              return (cardA: IndexCard, cardB: IndexCard) => {
                if (
                  cardA?.type !== option?.type ||
                  cardB?.type !== option?.type
                ) {
                  return 0;
                }

                return sort(
                  String(cardA[option.field]),
                  String(cardB[option.field]),
                  option.order,
                );
              };
            }
          }
        }
      })
      .filter((value) => value) ?? []
  );
};

export const sortCards = (
  cards: { id: string; data: IndexCard }[],
  sortingFunction?: ((cardA: IndexCard, cardB: IndexCard) => number) | null,
) => {
  if (!sortingFunction) {
    return cards;
  }
  return cards?.toSorted(({ data: cardA }, { data: cardB }) => {
    return sortingFunction(cardA, cardB);
  });
};
